import {
  CommonActions,
  RouteProp,
  StackActions,
  useNavigation,
  useRoute
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/native';
import ActivityIndicatorScreen from '../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../components/AlertBox';
import { FetchError } from '../../../api/api';
import { StudyParticipant } from '../../../models/StudyParticipant';
import { useLogout } from '../../../hooks/user/useLogout';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import { DrawerParamList } from '../../../navigation/DrawerNavigator';

const StyledViewContainer = styled.View`
  height: 100%;
`;

const ASSIGN_STUCK_MS = 30_000;

type AssignStudyTemplateProps<T> = FormElementProps<
  T,
  AssignStudyTemplateConfig
>;

export const AssignStudyTemplate = <T extends object>({
  element,
  stateNode
}: AssignStudyTemplateProps<T>) => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { studyTemplateId, redirectPath, redirectFormTemplateId } =
    element.config || {};
  const route = useRoute<RouteProp<DrawerParamList, 'FormTemplate'>>();
  const routeStudyParticipantId = route.params?.studyParticipantId;
  const { formValues, studyParticipantId, emitEvent, runService } =
    useDynamicFormContext<{ id?: number; studyParticipantId: number }, any>();
  const { logout } = useLogout();
  const participantId =
    formValues?.studyParticipantId ??
    studyParticipantId ??
    routeStudyParticipantId;

  const [updatedStudyParticipant, setUpdatedStudyParticipant] =
    useState<StudyParticipant>();
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<
    FetchError | Error | undefined
  >();
  const [stuckError, setStuckError] = useState<string | undefined>();
  const abortRef = useRef<AbortController | undefined>(undefined);

  // Abort the in-flight assign on unmount only — leaving the screen cancels the
  // service via its AbortSignal.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Assign the study template through the state-machine invoke service. The
  // service owns the API call, retry/backoff, and AbortSignal cancellation
  // (CD-207) — replacing the previous useAssignStudyTemplate hook +
  // hasAssignedRef + manual-cancel dance. The AbortController doubles as the
  // single-fire guard.
  useEffect(() => {
    if (!studyTemplateId || !participantId) return;
    if (!formValues?.id && !routeStudyParticipantId && !studyParticipantId) {
      return;
    }
    if (abortRef.current) return; // already assigning / assigned

    const controller = new AbortController();
    abortRef.current = controller;
    setStuckError(undefined);
    setAssignError(undefined);
    setAssigning(true);
    // Capture finality before the await; the machine can transition during the
    // in-flight assign, so a post-await check could miss the 'final' emit.
    const wasFinal = stateNode?.type === 'final';

    (async () => {
      try {
        const result = (await runService('assignStudyTemplate', {
          data: { studyParticipantId: participantId, studyTemplateId },
          signal: controller.signal
        })) as StudyParticipant;
        if (controller.signal.aborted) return;
        setUpdatedStudyParticipant(result);
        if (wasFinal) {
          await emitEvent('final', { transitions: [] });
        }
      } catch (err: any) {
        if (controller.signal.aborted) return;
        // allow a retry on a later dep change
        abortRef.current = undefined;
        setAssignError(err);
        if (err?.error === 'Unauthorized' || err?.statusCode === 401) {
          await logout();
        }
      } finally {
        if (!controller.signal.aborted) setAssigning(false);
      }
    })();
  }, [
    runService,
    emitEvent,
    logout,
    studyTemplateId,
    stateNode?.type,
    formValues?.id,
    studyParticipantId,
    routeStudyParticipantId,
    participantId
  ]);

  useEffect(() => {
    if (!studyTemplateId || !participantId) return;
    const timer = setTimeout(() => {
      // fire whenever there is no outcome yet — including a still-in-flight
      // assign (the retry chain can legitimately run past 30s)
      if (!updatedStudyParticipant && !assignError && !stuckError) {
        setStuckError(
          'This step is taking longer than expected. Please go back and try again.'
        );
      }
    }, ASSIGN_STUCK_MS);
    return () => clearTimeout(timer);
  }, [
    studyTemplateId,
    participantId,
    updatedStudyParticipant,
    assignError,
    stuckError
  ]);

  // redirect after assigning study template
  useEffect(() => {
    if (updatedStudyParticipant) {
      setStuckError(undefined);
      if (redirectPath !== 'PreviousScreen') {
        if (redirectPath === 'FormTemplate') {
          navigation.push('FormTemplate', {
            formTemplateId: redirectFormTemplateId
            // TODO: consider adding a configuration to:
            // Option 1: Start new form
            // Option 2: Load last edited form if exists
          });
          return;
        } else if (redirectPath === 'Study') {
          // Navigate to the Study Screen with back button to My Studies
          navigation.dispatch(
            CommonActions.reset({
              index: 0,
              routes: [
                {
                  name: 'DrawerNavigator',
                  state: {
                    index: 0,
                    routes: [
                      {
                        name: 'BottomTabNav',
                        state: {
                          index: 0,
                          routes: [
                            {
                              name: 'StudiesNav',
                              state: {
                                index: 1,
                                routes: [
                                  { name: 'MyStudies' },
                                  {
                                    name: 'StudyInfo',
                                    params: {
                                      studyId: updatedStudyParticipant.studyId,
                                      studyParticipantId:
                                        updatedStudyParticipant.id
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            })
          );
          return;
        } else if (redirectPath === 'MyData') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'MyDataNav'
              }
            })
          );
          return;
        } else if (redirectPath === 'StudiesNav') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'StudiesNav'
              }
            })
          );
          return;
        } else if (redirectPath === 'StudyComingSoon') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'StudiesNav',
                params: {
                  screen: 'StudyComingSoon',
                  initial: false
                }
              }
            })
          );
          return;
        } else if (redirectPath === 'ToDoNav') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'ToDoNav'
              }
            })
          );
          return;
        }
      }
      // default to the previous screen prior to the FormTemplate
      const navigationState = navigation.getState();
      let popCount = 0;
      for (let i = navigationState.routes.length - 1; i >= 0; i--) {
        if (
          navigationState.routes[i].name !== 'StudyEventForm' &&
          navigationState.routes[i].name !== 'FormTemplate'
        ) {
          navigation.pop(popCount);
          return;
        }
        popCount++;
      }
      // else navigate to My Chloe
      navigation.dispatch(
        StackActions.replace('DrawerNavigator', {
          screen: 'BottomTabNav',
          params: {
            screen: 'HomeNav'
          }
        })
      );
    }
  }, [
    navigation,
    redirectPath,
    redirectFormTemplateId,
    updatedStudyParticipant
  ]);
  const displayError = stuckError || (assignError as any)?.message;
  const showSpinner =
    assigning ||
    (!!updatedStudyParticipant && !displayError) ||
    (!!studyTemplateId &&
      !!participantId &&
      !formValues?.id &&
      !routeStudyParticipantId &&
      !studyParticipantId);

  return (
    <StyledViewContainer>
      {showSpinner ? (
        <ActivityIndicatorScreen backgroundColor='transparent' />
      ) : displayError ? (
        <AlertBox severity='error'>{displayError}</AlertBox>
      ) : (
        <AlertBox severity='error'>
          Attempting to assign a study template. However, no study template was
          specified. Please contact the study administrator.
        </AlertBox>
      )}
    </StyledViewContainer>
  );
};

interface AssignStudyTemplateConfig {
  studyTemplateId?: number;
  redirectPath?: string;
  redirectFormTemplateId?: number;
}

export const AssignStudyTemplatePlugin: FormElementPlugin<
  any,
  AssignStudyTemplateConfig
> = {
  name: 'Assign Study Template',
  type: 'AssignStudyTemplate',
  effectOnMount: true,
  element: AssignStudyTemplate as React.FC<
    FormElementProps<any, AssignStudyTemplateConfig>
  >
};
